import { Card, Chip, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { TpmsDevice } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { fDateTime } from "src/utils/format-time";
import { deviceAlertCount, hasTpmsReported } from "src/utils/tpms-utils";

type TpmsDeviceRow = TpmsDevice & {
  sensorCount: number;
  alertCount: number;
};

type TpmsDevicesProps = {
  tpmsDevices?: TpmsDevice[],
  onTpmsDeviceSelected: (tpmsDevice: TpmsDevice) => void
}

const TpmsDevices = memo(({ tpmsDevices, onTpmsDeviceSelected }: TpmsDevicesProps) => {
  const rows: TpmsDeviceRow[] = tpmsDevices?.map(tpmsDevice => ({
    ...tpmsDevice,
    sensorCount: tpmsDevice.sensors?.length || 0,
    alertCount: deviceAlertCount(tpmsDevice.sensors || []),
  })) || [];
  const { sortedList, sortData } = useSort(rows, "deviceName");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["deviceName", "vehicleType"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="TPMS Devices" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="deviceName" sortData={sortData} >
                Name
              </SortableHeader>

              <SortableHeader sortKey="vehicleType" sortData={sortData} >
                Vehicle Type
              </SortableHeader>

              <SortableHeader sortKey="sensorCount" sortData={sortData} >
                Sensors
              </SortableHeader>

              <SortableHeader sortKey="alertCount" sortData={sortData} >
                Alerts
              </SortableHeader>

              <SortableHeader sortKey="lastReportedAt" sortData={sortData} >
                Last Report
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredList.map((tpmsDevice) =>
            (<TableRow
              key={tpmsDevice.id}
              hover
              onClick={() => onTpmsDeviceSelected(tpmsDevice)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {tpmsDevice.deviceName}
              </TruxTableCell>

              <TruxTableCell sx={{ textTransform: "capitalize" }}>
                {tpmsDevice.vehicleType}
              </TruxTableCell>

              <TruxTableCell>
                {tpmsDevice.sensorCount}
              </TruxTableCell>

              <TruxTableCell>
                {tpmsDevice.alertCount > 0
                  ? <Chip size="small" color="error" label={tpmsDevice.alertCount} />
                  : <Chip size="small" variant="outlined" label="0" />}
              </TruxTableCell>

              <TruxTableCell>
                {hasTpmsReported(tpmsDevice.lastReportedAt) ? fDateTime(tpmsDevice.lastReportedAt) : "Never"}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div>;
});

type PageModel = {
  tpmsDevices?: TpmsDevice[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    tpmsDevices: axios.get(endpoints.tpmsDevices.findAll).then(response => response.data),
  }), []);
  const {
    data,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  const onTpmsDeviceSelected = useCallback((tpmsDevice: TpmsDevice) => {
    router.push(`/app/tpms/${tpmsDevice.id}`);
  }, [router]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.tpmsDevices?.length !== 0 &&
          <PageHeader title="TPMS" />
        }

        {data && data?.tpmsDevices?.length === 0 &&
          <NoneFound
            title="No TPMS Devices Found"
            message="TPMS devices appear here once registered and assigned to your organization."
          />
        }

        {data && data?.tpmsDevices?.length !== 0 &&
          <TpmsDevices tpmsDevices={data.tpmsDevices} onTpmsDeviceSelected={onTpmsDeviceSelected} />}
      </DisplayPageData>
    </Container>
  );
}
